import * as React from 'react';
import PropTypes from 'prop-types';
import {useTheme, styled} from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import Autocomplete, {autocompleteClasses} from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import ReactDOM from "react-dom";
import {
    TableSortLabel,
    IconButton
} from "@mui/material";

import {Document, Drawing} from "../Components/BaseTypes";
import TransmittalBase from "./TransmittalBase.tsx";
import {forwardRef, useImperativeHandle} from "react";
import {Attachment} from "./Attachment.tsx";

const StyledAutocompletePopper = styled('div')(({theme}) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: 'none',
        margin: 0,
        color: 'inherit',
        fontSize: 13,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            minHeight: 'auto',
            alignItems: 'flex-start',
            padding: 8,
            borderBottom: `1px solid  ${
                theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'
            }`,
            '&[aria-selected="true"]': {
                backgroundColor: 'transparent',
            },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
                {
                    backgroundColor: theme.palette.action.hover,
                },
        },
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: 'relative',
    },
}));

function PopperComponent(props) {
    const {disablePortal, anchorEl, open, ...other} = props;

    return <StyledAutocompletePopper {...other} />;
}

PopperComponent.propTypes = {
    anchorEl: PropTypes.any,
    disablePortal: PropTypes.bool,
    open: PropTypes.bool.isRequired,
};

const StyledPopper = styled(Popper)(({theme}) => ({
    border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
    boxShadow: `0 8px 24px ${
        theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
    }`,
    borderRadius: 6,
    width: 300,
    zIndex: 100001,
    fontSize: 13,
    color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}));

const StyledInput = styled(InputBase)(({theme}) => ({
    padding: 10,
    width: '100%',
    borderBottom: `1px solid ${
        theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
    }`,
    '& input': {
        borderRadius: 4,
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
        padding: 8,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
        fontSize: 14,
        '&:focus': {
            boxShadow: `0px 0px 0px 3px ${
                theme.palette.mode === 'light'
                    ? 'rgba(3, 102, 214, 0.3)'
                    : 'rgb(12, 45, 107)'
            }`,
            borderColor: theme.palette.mode === 'light' ? '#0366d6' : '#388bfd',
        },
    },
}));


/**
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */

interface ItemSelectionPropTypes {
    selectedDrawingIds?: number[],
    selectedDocumentIds?: number[],
    availableDrawingsLookup?: Record<number, Drawing>,
    availableDocumentsLookup?: Record<number, Document>,
    docTypesLookup?: Record<number, object>,
    onChange: (v) => void,
    availableEntitySetter: (value: Record<number, Drawing | Document>) => void
}

export const TransmittalItemSelection = forwardRef(({availableEntitySetter, ...props}: ItemSelectionPropTypes, ref) => {

    // console.log('render entity list');

    const onChange = (v) => {
        setValue(v);
        props.onChange(v);
    }

    let dwgLabels = [];
    let docLabels = [];
    let labels = [];
    let category = '';
    const selectedDrawingIds = props.selectedDrawingIds?.filter(id => id > 0) || [];
    const selectedDocumentIds = props.selectedDocumentIds?.filter(id => id > 0) || [];
    let selectedValues = [];

    if (props.availableDrawingsLookup) {
        dwgLabels = Object.values(props.availableDrawingsLookup).map(dwg => ({
            key: dwg.id + 'Drawing',
            'name': dwg.dwgMelDwgNo,
            id: dwg.id,
            category: 'Drawing',
            'description': dwg.dwgName,
            type: 'Drawing',
            cliCode: dwg.dwgCliDwgNo
        }));
        labels = dwgLabels;
        category = 'Drawing';

        selectedValues =
            selectedDrawingIds.map(id => dwgLabels.find(dwgLabel => (dwgLabel.id == id)) || {
                key: id + 'Drawing',
                'name': 'Not Found For Job/Project',
                id: id,
                category: 'Drawing',
                description: '',
                type: 'Drawing'
            });
    }
    if (props.availableDocumentsLookup) {
        docLabels = Object.values(props.availableDocumentsLookup).map(doc => ({
            'description': doc.dorName,
            'name': doc.dorMelDocNo,
            id: doc.id,
            category: 'Document',
            key: doc.id + 'Document',
            type: props.docTypesLookup[doc.dorDotCode]?.dotName || 'Unknown'
        }));
        labels = docLabels;
        category = 'Document';

        selectedValues =
            selectedDocumentIds.map(id => docLabels.find(docLabel => (docLabel.id == id)) || {
                key: id + 'Document',
                'name': 'Not Found For Job/Project',
                id: id,
                type: 'Drawing',
                category: 'Document',
                description: ''
            });

    }
    // let labels = [...dwgLabels, ...docLabels]

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [value, setValue] = React.useState(selectedValues);


    useImperativeHandle(ref, () => {
        return {
            updateItemsFromAttachedItemIds: (attachedItemIds: number[]) => {
                attachedItemIds.forEach(id => {
                    if (value.find(value => value.id === id)) return;


                    if (props.availableDocumentsLookup) {
                        value.push(docLabels.find(docLabel => (docLabel.id == id)));
                        // setValue([...value, ...attachedItemIds.map(id => docLabels.find(docLabel => (docLabel.id == id)))])
                    }
                    if (props.availableDrawingsLookup) {
                        value.push(dwgLabels.find(v => (v.id == id)));
                        // setValue([...value, ...attachedItemIds.map(id => dwgLabels.find(dwgLabel => (dwgLabel.id == id)))])
                    }
                })
                setValue([...value])
            },
            getSelectedIds: () => value.map(v => v.id)
        };
    }, [value]);


    const [pendingValue, setPendingValue] = React.useState([]);


    const valuesLookup = {};
    value.forEach(v => {
        valuesLookup[v.key] = v;
    });


    const theme = useTheme();

    const handleClick = (event) => {
        setPendingValue(value);
        setAnchorEl(event.currentTarget);
        //@ts-expect-error Function defined as html include
        handleChangeEvent();
    };

    const removeValue = (valueToRemove) => {
        onChange(value.filter(v => v.key !== valueToRemove.key));
        //@ts-expect-error Function defined as html include
        handleChangeEvent();
    }

    const handleClose = () => {
        // setValue(pendingValue);
        if (anchorEl) {
            anchorEl.focus();
        }
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'github-label' : undefined;

    return (
        <React.Fragment>
            <Box sx={{paddingRight: '0'}} className={'floatRight'}>
                <button className={'button'} type={'button'} disableRipple aria-describedby={id} onClick={handleClick}>
                    <span>Add {category}</span>
                </button>
            </Box>
            <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="left">
                <ClickAwayListener onClickAway={handleClose}>
                    <div>
                        <Box
                            sx={{
                                borderBottom: `1px solid ${
                                    theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
                                }`,
                                padding: '8px 10px',
                                fontWeight: 600,
                            }}
                        >
                            Select {category}

                        </Box>
                        <Autocomplete
                            ListboxProps={{style: {maxHeight: "70vh"}}}
                            open
                            multiple
                            onClose={(event, reason) => {
                                if (reason === 'escape') {
                                    handleClose();
                                }
                            }}
                            value={pendingValue}
                            onChange={(event, newValue, reason) => {
                                if (
                                    event.type === 'keydown' &&
                                    event.key === 'Backspace' &&
                                    reason === 'removeOption'
                                ) {
                                    return;
                                }
                                if (newValue.length > value.length) {
                                    //move the last item to first if adding a new item
                                    //so, new items get added to the top
                                    newValue.unshift(newValue.pop());
                                }
                                setPendingValue(newValue);
                                onChange(newValue);

                            }}
                            disableCloseOnSelect
                            // PopperComponent={Popper}
                            // PopperComponent={StyledAutocompletePopper}
                            PopperComponent={PopperComponent}
                            // groupBy={(option) => option.category}
                            renderTags={() => null}
                            noOptionsText="No Drawings/Documents"
                            isOptionEqualToValue={(option, value) => option.key == value.key}
                            renderOption={(props, option, {selected}) => (
                                <li {...props} key={option.category + option.id}>
                                    <Box
                                        component={DoneIcon}
                                        sx={{width: 17, height: 17, mr: '5px', ml: '-2px'}}
                                        style={{
                                            visibility: selected ? 'visible' : 'hidden',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                            '& span': {
                                                color:
                                                    theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                                            },
                                        }}
                                    >
                                        {option.name}
                                        <br/>
                                        <span>{option.description}</span>
                                    </Box>
                                    <Box
                                        component={CloseIcon}
                                        sx={{opacity: 0.6, width: 18, height: 18}}
                                        style={{
                                            visibility: selected ? 'visible' : 'hidden',
                                        }}
                                    />
                                </li>
                            )}
                            options={labels}
                            getOptionLabel={(option) => `${option.name} ${option.description}`}
                            // getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                                <StyledInput
                                    ref={params.InputProps.ref}
                                    inputProps={params.inputProps}
                                    autoFocus
                                    placeholder="Filter Drawings/Documents"
                                />
                            )}
                        />
                    </div>
                </ClickAwayListener>
            </StyledPopper>
            {category === 'Drawing' &&
                (
                    <>
                        <h5 style={{
                            margin: '0',
                            paddingLeft: '0.5em',
                            lineHeight: '2.2em',
                            backgroundColor: '#EEEEEE'
                        }}>Drawings</h5>
                        <ItemsTable value={value} category={category} removeValue={removeValue} inputName={'drawing'}
                                    availableEntityLookup={props.availableDrawingsLookup}
                                    availableEntitySetter={availableEntitySetter}
                        />
                    </>
                )
            }
            {category === 'Document' &&
                (
                    <>
                        <h5 style={{
                            margin: '0',
                            paddingLeft: '0.5em',
                            lineHeight: '2.2em',
                            backgroundColor: '#EEEEEE'
                        }}>Documents</h5>
                        <ItemsTable value={value} category={category} removeValue={removeValue} inputName={'doc'}
                                    availableEntityLookup={props.availableDocumentsLookup}
                                    availableEntitySetter={availableEntitySetter}
                        />
                    </>
                )

            }
        </React.Fragment>
    );
});

function ItemsTable({value, availableEntityLookup, availableEntitySetter, category, ...props}: {
    value: object[],
    category: 'Drawing' | 'Document',
    removeValue: (object) => void,
    inputName: string,
    availableEntityLookup: Record<string, Document | Drawing>,
    availableEntitySetter: (value: Record<string, Document | Drawing>) => void
}) {
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
    const [sortBy, setSortBy] = React.useState(null);

    // console.log('render table');


    const sortData = (values, sortBy, sortOrder) => {
        if (!sortBy) return values;
        return values.sort((i, j) => {
            if (i[sortBy] < j[sortBy]) {
                return sortOrder === "asc" ? -1 : 1;
            } else {
                if (i[sortBy] > j[sortBy]) {
                    return sortOrder === "asc" ? 1 : -1;
                } else {
                    return 0;
                }
            }
        })

    }
    const requestSort = (pSortBy) => {
        let _sortBy = sortBy;
        let _sortOrder = sortOrder;
        return event => {
            if (pSortBy === sortBy) {
                _sortOrder = _sortOrder === "asc" ? "desc" : "asc";
            } else {
                _sortBy = pSortBy;
                _sortOrder = "asc";
            }
            setSortBy(_sortBy);
            setSortOrder(_sortOrder);
        };
    }

    const attachmentsProcessed = [];

    return (
        <table className={'styled'} style={{width: '100%'}}>
            <thead>
            <tr>
                <th style={{width: '20em'}}>
                    <TableSortLabel
                        active={sortBy === "name"}
                        direction={sortOrder}
                        onClick={requestSort("name")}
                    >
                        {category} Code
                    </TableSortLabel>
                </th>
                {category === 'Drawing' && <th>
                    <TableSortLabel
                        active={sortBy === "cliCode"}
                        direction={sortOrder}
                        onClick={requestSort("cliCode")}
                    >
                        Client Code
                    </TableSortLabel>
                </th>}
                <th style={{width: '10em'}}>
                    <TableSortLabel
                        active={sortBy === "type"}
                        direction={sortOrder}
                        onClick={requestSort("type")}
                    >
                        Type
                    </TableSortLabel>
                </th>
                <th>
                    <TableSortLabel
                        active={sortBy === "description"}
                        direction={sortOrder}
                        onClick={requestSort("description")}
                    >
                        Title
                    </TableSortLabel>
                </th>
                <th>Attachment</th>
                <th style={{width: '2em'}}></th>
            </tr>
            </thead>
            <tbody>
            {sortData(value, sortBy, sortOrder).map((item, index) => {
                    const code = item.name;
                    const attachments = availableEntityLookup[item.id]?.fileAttachments || [];

                    // console.log(attachments);

                    return <tr key={JSON.stringify({...item, ...attachments})}>
                        <td>{item.name}
                            <input type={'hidden'} name={'trans_' + props.inputName + 'ID[' + index + ']'}
                                   value={item.id}/>
                        </td>
                        {category === 'Drawing' && <td>{item.cliCode}</td>}
                        <td>{item.type}</td>
                        <td>{item.description}</td>
                        <td>
                            {attachments.map((attachment) => {
                                    const renderInputs = !attachmentsProcessed.includes(attachment.id);
                                    attachmentsProcessed.push(attachment.id);

                                    return <>
                                        <Attachment
                                            renderInputs={renderInputs}
                                            attachment={attachment} key={attachment.id} onDelete={() => {
                                            availableEntityLookup[item.id].fileAttachments = availableEntityLookup[item.id].fileAttachments.filter((v) => v.id != attachment.id);
                                            availableEntitySetter({...availableEntityLookup});
                                            //@ts-expect-error Function defined as html include
                                            handleChangeEvent();
                                        }}/>
                                        {category == 'Drawing' &&
                                            <input type={'hidden'}
                                                   name={`attachments[${attachment.id}][drawings][]`}
                                                   value={item.id}/>}
                                        {category == 'Document' &&
                                            <input type={'hidden'}
                                                   name={`attachments[${attachment.id}][docs][]`}
                                                   value={item.id}/>
                                        }
                                    </>
                                }
                            )}
                        </td>
                        <td>
                            <IconButton size={'small'}
                                        onClick={() => props.removeValue(item)}
                            ><CloseIcon fontSize={'small'}/></IconButton>
                        </td>
                    </tr>
                }
            )}
            {!value || !value.length ? (<tr key={'empty'}>
                <td colSpan={4}>No items found, please add as necessary</td>
            </tr>) : ''}
            </tbody>
            {/*<TableFooter/>*/}
        </table>
    )
}

// From https://github.com/abdonrd/github-labels

window['renderTransmittalItemSelectionWidget'] = function (transmittalDto) {
    history.replaceState(null, '', window.location.href.replace('&openSubmission=1', ''));
    ReactDOM.render(<TransmittalBase
            {...transmittalDto}
        />,
        document.querySelector('#transmittalBaseWidget'));

}
